// ============================================================================
// Market P&L data (board item #1027). useEffect loaders — no TanStack here.
//
//   useMarketPlViewer()  calls is_market_pl_viewer() ONCE; the Dashboard shows
//                        its Market P&L tab only when it is true. RLS
//                        on the two tables is the hard boundary — this only
//                        decides what to show.
//   useMarketPlData()    v_market_pl_revenue + v_market_pl_labor_lines +
//                        market_pl_assumptions, and who last edited them.
//   save* helpers        every write through commitWrite, so a write the
//                        database refuses (not finance) says so.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../../shared/supabase';
import { errorMessage } from '../../../../shared/errors';
import { commitWrite, useReader } from '../../../../shared/save-path';
import {
  ASSUMPTION_KEYS,
  STANDARDS,
  type Assumptions,
  type LaborLine,
  type LineRole,
  type MarketInput,
} from './market-pl-math';

export const MARKET_PL_TABLES = ['market_pl_assumptions', 'market_pl_labor_allocation'] as const;

const REVENUE_SELECT =
  'market_name, dot_color, sites_active, revenue_annual, sub_annual, avg_monthly_clinic_asc, avg_monthly_dialysis, sites_signed, signed_revenue_annual, signed_sub_annual, sites_signed_uncosted';
const LINES_SELECT =
  'market_name, allocation_id, employee_id, display_name, role, share, annual_cost_override, annual_cost_base, note';

const n = (v: unknown): number => (v == null ? 0 : Number(v));
const nOrNull = (v: unknown): number | null => (v == null ? null : Number(v));

/** null while asking; then true/false. One call per dashboard mount. */
export function useMarketPlViewer(enabled: boolean): boolean | null {
  const [viewer, setViewer] = useState<boolean | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc('is_market_pl_viewer');
      if (cancelled) return;
      if (error) {
        console.error('[market-pl] is_market_pl_viewer failed', error);
        setViewer(false);
        return;
      }
      setViewer(data === true);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return viewer;
}

export interface LineRow extends LaborLine {
  marketName: string;
  annualCostBase: number;
}

export interface MarketPlData {
  markets: MarketInput[];
  lines: LineRow[];
  assumptions: Assumptions;
  updatedAt: string | null;
  updatedByName: string | null;
}

export function useMarketPlData(enabled: boolean) {
  const [data, setData] = useState<MarketPlData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((t) => t + 1), []);
  useReader(MARKET_PL_TABLES, refetch);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const [rev, lines, asm] = await Promise.all([
          supabase.from('v_market_pl_revenue').select(REVENUE_SELECT),
          supabase.from('v_market_pl_labor_lines').select(LINES_SELECT),
          supabase
            .from('market_pl_assumptions')
            .select('*')
            .eq('assumptions_id', 1)
            .maybeSingle(),
        ]);
        if (rev.error) throw rev.error;
        if (lines.error) throw lines.error;
        if (asm.error) throw asm.error;
        if (!asm.data) throw new Error('The Market P&L standards row is missing.');

        const a = { ...STANDARDS };
        const row = asm.data as Record<string, unknown>;
        for (const k of ASSUMPTION_KEYS) a[k] = n(row[k]);

        let updatedByName: string | null = null;
        const eid = row.updated_by_eid as string | null;
        if (eid) {
          const who = await supabase
            .from('v_employee_display')
            .select('employee_full_name')
            .eq('employee_id', eid)
            .maybeSingle();
          if (who.error) throw who.error;
          updatedByName = (who.data?.employee_full_name as string | null) ?? null;
        }

        const lineRows: LineRow[] = (lines.data ?? []).map((l) => ({
          marketName: l.market_name as string,
          allocationId: l.allocation_id as string,
          employeeId: (l.employee_id as string | null) ?? null,
          displayName: (l.display_name as string | null) ?? '—',
          role: l.role as LineRole,
          share: n(l.share),
          override: nOrNull(l.annual_cost_override),
          note: (l.note as string | null) ?? null,
          annualCostBase: n(l.annual_cost_base),
        }));

        const markets: MarketInput[] = (rev.data ?? [])
          .map((r) => ({
            marketName: r.market_name as string,
            dotColor: r.dot_color as string,
            sitesActive: n(r.sites_active),
            sitesSigned: n(r.sites_signed),
            sitesSignedUncosted: n(r.sites_signed_uncosted),
            revenueAnnual: n(r.revenue_annual),
            subAnnual: n(r.sub_annual),
            signedRevenueAnnual: n(r.signed_revenue_annual),
            signedSubAnnual: n(r.signed_sub_annual),
            avgMonthlyClinicAsc: nOrNull(r.avg_monthly_clinic_asc),
            avgMonthlyDialysis: nOrNull(r.avg_monthly_dialysis),
            lines: lineRows.filter((l) => l.marketName === r.market_name),
          }))
          .sort((x, y) => y.revenueAnnual - x.revenueAnnual);

        if (cancelled) return;
        setData({
          markets,
          lines: lineRows,
          assumptions: a,
          updatedAt: (row.updated_at as string | null) ?? null,
          updatedByName,
        });
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Could not load the Market P&L.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, tick]);

  return { data, isLoading, error, refetch };
}

export async function saveAssumptions(patch: Partial<Assumptions>, myEid: string | null) {
  await commitWrite({
    label: 'save the Market P&L standards',
    write: supabase
      .from('market_pl_assumptions')
      .update({ ...patch, updated_by_eid: myEid })
      .eq('assumptions_id', 1)
      .select('assumptions_id'),
    touches: MARKET_PL_TABLES,
  });
}

export interface NewLine {
  employeeId: string | null;
  label: string | null;
  role: LineRole;
  marketName: string;
  share: number;
  override: number | null;
  note: string | null;
}

export async function addAllocationLine(line: NewLine, myEid: string | null) {
  await commitWrite({
    label: 'add the labor line',
    write: supabase
      .from('market_pl_labor_allocation')
      .insert({
        employee_id: line.employeeId,
        label: line.employeeId ? null : line.label,
        role: line.role,
        market_name: line.marketName,
        share: line.share,
        annual_cost_override: line.override,
        note: line.note,
        created_by_eid: myEid,
        updated_by_eid: myEid,
      })
      .select('allocation_id'),
    touches: MARKET_PL_TABLES,
  });
}

export async function updateAllocationLine(
  allocationId: string,
  patch: { share?: number; annual_cost_override?: number | null; note?: string | null; effective_end?: string },
  myEid: string | null,
  label = 'change the labor line',
) {
  await commitWrite({
    label,
    write: supabase
      .from('market_pl_labor_allocation')
      .update({ ...patch, updated_by_eid: myEid })
      .eq('allocation_id', allocationId)
      .select('allocation_id'),
    touches: MARKET_PL_TABLES,
  });
}

export interface PersonOption {
  employeeId: string;
  name: string;
}

/**
 * People a line can be charged to. Reads v_employee_display filtered to Active
 * rather than v_employee_picker on purpose: the picker is salaried-only, and the
 * night managers this view charges (Angie, Armando, William) are hourly. Only
 * the finance capability opens this list.
 */
export function usePeopleOptions(enabled: boolean): PersonOption[] {
  const [people, setPeople] = useState<PersonOption[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('v_employee_display')
        .select('employee_id, employee_full_name')
        .eq('employment_status', 'Active')
        .order('employee_full_name');
      if (cancelled) return;
      if (error) {
        console.error('[market-pl] people list failed', error);
        return;
      }
      setPeople(
        (data ?? []).map((p) => ({
          employeeId: p.employee_id as string,
          name: (p.employee_full_name as string | null) ?? (p.employee_id as string),
        })),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return people;
}
